import chartHelper from 'components/chart/chart-helper'
import {
  ChartConfig,
  ChartDataCandlestick,
  ChartDataLine,
  ChartDataLineInside,
  ChartDirection,
  ChartInitConfig,
  ChartOptions,
  ChartTimeRange,
  ChartType,
} from 'models/chart'
import { MarketHistory } from 'models/market'
import { formatNumber } from 'utils/format-number'
import { promiseWrapper } from 'utils/promise'
import './import-jquery'
import { GoogleAnalytics } from 'models/google-analitics'

import 'flot/jquery.flot.js'
import 'flot/jquery.flot.time.js'
import 'flot/jquery.flot.candlestick.js'
import 'flot/jquery.flot.resize.js'
import { createCandlestick } from 'flot/jquery.flot.jumware.js'
import { t9n } from 't9n/t9n'

declare let USER_BASE_URL_IK: string
declare let $: any
declare let ga: (type: string, option: GoogleAnalytics) => void

const formatDate = (date: Date, format: string): string => {
  const monthNames = t9n('chart.monthNames')
  const dayNames = t9n('chart.monthNames')
  return $.plot.formatDate(date, format, monthNames, dayNames)
}

const initialChartConfig: ChartConfig = {
  element: null,
  currency: null,
  bank: 'ik',
  timeRange: '1-day',
  chartType: 'line',
  direction: 'avg',
}

const chart = {
  plot: null,
  data: null,
  config: initialChartConfig,

  init(config: ChartInitConfig, isMarketBriefListener = true): void {
    chart.config = {
      ...initialChartConfig,
      ...config,
    }

    // Redraw every 15min
    setInterval(chart.singleChart, 900000)

    if (isMarketBriefListener) window.addEventListener('pairMarketBrief fetched', chart.updateFromMarketBrief)

    chart.singleChart()
    chart.initListeners()
  },

  initListeners(): void {
    const onResize = (): void => {
      if (!chart.plot) {
        return
      }

      const placeholder = chart.plot.getPlaceholder()
      if (placeholder.width() === 0 || placeholder.height() === 0) {
        return
      }

      chart.plot.resize()
      chart.plot.setupGrid()
      chart.plot.draw()
    }
    $(window).on('resize', onResize)
    $(window).trigger('resize')

    const handleChartTime = (): void => {
      const selectedValue = $('#new-chart-time').val() as ChartTimeRange
      chart.config.timeRange = selectedValue
      chart.singleChart()
    }
    $('#new-chart-time').on('change', handleChartTime)

    const handleActualExchange = (): void => {
      const selectedValue = $('#chart-exchange-type').val() as ChartDirection
      chart.config.direction = selectedValue
      chart.singleChart()

      if (typeof ga !== 'undefined') {
        ga('send', {
          hitType: 'event',
          eventCategory: 'experimentsIK__wykres',
          eventAction: 'chart-type-changes',
          eventLabel: selectedValue,
        })
      }
    }

    $('#chart-exchange-type').on('change', handleActualExchange)

    const changeChartType = (chartType: ChartType): void => {
      chart.config.chartType = chartType
      chart.singleChart()
    }
    const handleChartType = (): void => {
      const selectedValue = ($('#new-chart-types').val() || 'line') as ChartType
      changeChartType(selectedValue)
    }
    $('#new-chart-types').on('change', handleChartType)
  },

  showLoading(): void {
    $(`#${chart.config.element} .single-chart-loading`).removeClass('is-hidden')
  },

  hideLoading(): void {
    $(`#${chart.config.element} .single-chart-loading`).addClass('is-hidden')
  },

  singleChart(): void {
    chart.showLoading()

    const urlBeginNBP = `${USER_BASE_URL_IK}/api/public/nbpHistory/`

    const urlBegin = `${USER_BASE_URL_IK}/api/public/marketHistory/`
    const sampleSpan = chartHelper.getSampleSpanByTimeRange(chart.config.timeRange)

    const url =
      chart.config.bank === 'nbp'
        ? `${urlBeginNBP}${chart.config.currency}_PLN/${chart.config.timeRange}/`
        : `${urlBegin}${chart.config.currency}_PLN/${chart.config.timeRange}/${sampleSpan}`

    promiseWrapper<MarketHistory, undefined>(`${url}`, 'GET')
      .then((data) => {
        chart.data = data
        chart.renderGraph(data)

        if (chart.config.bank === 'ik') {
          chart.updateFromMarketBrief()
        }

        chart.hideLoading()
      })
      .catch(() => {
        const $offlineBox = document.querySelector<HTMLDivElement>('[data-offline-box]')
        $offlineBox.hidden = false
      })
  },

  updateFromMarketBrief(): void {
    const pair = window.pairMarketBrief

    if (!pair) {
      return
    }

    const actualRate =
      chart.config.direction === 'avg'
        ? pair.directExchangeOffers.forexNow
        : chart.config.direction === 'buy'
          ? pair.directExchangeOffers.buyNow
          : pair.directExchangeOffers.sellNow
    $('#actual-type').html(formatNumber(actualRate, 4))

    const existingData: MarketHistory = chart.data
    if (!existingData || chart.config.chartType !== 'line') {
      return
    }

    const timestamp = Math.trunc(new Date(pair.ts).getTime() / 1000)
    const chartData: MarketHistory = {
      ...existingData,
      ...(chart.config.direction === 'avg' && {
        fx_series: [...existingData.fx_series.slice(0, -1), [timestamp, pair.directExchangeOffers.forexNow, 0, 0, 0]],
      }),
      ...((chart.config.direction === 'buy' || chart.config.direction === 'sell') && {
        ik_series: [
          ...existingData.ik_series.slice(0, -1),
          [timestamp, pair.directExchangeOffers.sellNow, 0, 0, 0, pair.directExchangeOffers.buyNow, 0, 0, 0],
        ],
      }),
    }

    chart.data = chartData

    chart.renderGraph(chartData)

    // TODO
    // chart.plot.setData(chartData)
    // chart.plot.draw()
  },

  renderGraph(data: MarketHistory): void {
    const chartData = chart.prepareDataToPlot(data, chart.config.chartType, chart.config.direction)
    const chartOptions = chart.getOptionToPlot(
      chart.config.chartType,
      chartHelper.timeFlotFormats[chart.config.timeRange],
    )
    const $chartElement = $(`#${chart.config.element}`)

    chart.plot = $.plot($chartElement, chartData, chartOptions)

    $chartElement.append(
      '<div class="single-chart-loading is-hidden"><div class="bem-spinner-icon spin-animation"></div></div>',
    )

    chart.renderTooltip()
  },

  renderTooltip(): void {
    let previousPoint = null
    $(`#${chart.config.element}`)
      .off('plothover')
      .on('plothover', (_, pos, item): void => {
        let content
        if (item) {
          if (item.found) {
            if (previousPoint !== item.datapoint) {
              previousPoint = item.datapoint

              $('#flot-tooltip').remove()
              content = chart.getContentTooltip(item, chart.config.chartType)
              chart.showTooltip(pos.pageX, pos.pageY, content)
            }
          } else if (previousPoint !== item.dataIndex) {
            previousPoint = item.dataIndex

            $('#flot-tooltip').remove()
            content = chart.getContentTooltip(item, chart.config.chartType)
            chart.showTooltip(item.pageX, item.pageY, content)
          }
        } else {
          $('#flot-tooltip').remove()
          previousPoint = null
        }
      })
  },

  showTooltip(x: number, y: number, contents: string): void {
    const isRightArea = $(`#${chart.config.element}`).width() / 2 < x - chart.plot.offset().left
    $(`<div id="flot-tooltip">${contents}</div>`)
      .css({
        position: 'absolute',
        display: 'none',
        top: y + 5,
        border: '3px solid #225763',
        'background-color': '#fff',
        opacity: 0.8,
        ...(isRightArea ? { left: x - 85 } : { left: x + 5 }),
      })
      .appendTo('body')
      .fadeIn(200)
  },

  getContentTooltip(item, chartType: ChartType): string {
    const timeFormat = chartHelper.timeTooltipFormats[chart.config.timeRange]

    const chartRate = t9n('chart.rate')
    const chartOpening = t9n('chart.opening')
    const chartLowest = t9n('chart.lowest')
    const chartClosing = t9n('chart.closing')
    const chartTime = t9n('chart.time')
    const chartHignest = t9n('chart.highest')

    if (chartType === 'line') {
      const x = item.datapoint[0]
      const y = formatNumber(item.datapoint[1], 4)
      const date = new Date(x)
      return `<b>${chartRate}: </b> ${y}<br/><b>${chartTime}: </b> ${formatDate(date, timeFormat)}`
    } else if (chartType === 'candlestick' || chartType === 'ohlc') {
      const date = new Date(item.label)
      return `<b>${chartOpening}: </b>${formatNumber(item.value[0], 4)}<br/><b>${chartHignest}: </b>${formatNumber(
        item.value[3],
        4,
      )}<br/><b>${chartLowest}: </b>${formatNumber(item.value[2], 4)}<br/><b>${chartClosing}: </b>${formatNumber(
        item.value[1],
        4,
      )}<br/><b>${chartTime}: </b> ${formatDate(date, timeFormat)}`
    }
  },

  prepareDataToPlot(
    data: MarketHistory,
    chartType: ChartType,
    direction: ChartDirection,
  ): ChartDataLine | ChartDataCandlestick {
    return chartType === 'candlestick' || chartType === 'ohlc'
      ? chart.prepareCandlestickData(data, direction)
      : chart.prepareLineData(data, direction)
  },

  prepareLineData(data: MarketHistory, direction: ChartDirection): ChartDataLine {
    let dataChart: ChartDataLineInside = []
    if (chart.config.bank === 'nbp') {
      dataChart = data.nbp_series.map((s) => [s[0] * 1000, s[1]])
    } else if (direction === 'avg') {
      dataChart = data.fx_series.map((s) => [s[0] * 1000, s[1]])
    } else if (direction === 'buy') {
      dataChart = data.ik_series.map((s) => [s[0] * 1000, s[5]])
    } else if (direction === 'sell') {
      dataChart = data.ik_series.map((s) => [s[0] * 1000, s[1]])
    }
    return [dataChart]
  },

  prepareCandlestickData(data: MarketHistory, direction: ChartDirection): ChartDataCandlestick {
    let dataChart: ChartDataCandlestick = []
    if (direction === 'avg') {
      dataChart = data.fx_series.map((s) => [s[0] * 1000, s[4], s[1], s[3], s[2]])
    } else if (direction === 'buy') {
      dataChart = data.ik_series.map((s) => [s[0] * 1000, s[8], s[5], s[7], s[6]])
    } else if (direction === 'sell') {
      dataChart = data.ik_series.map((s) => [s[0] * 1000, s[4], s[1], s[3], s[2]])
    }
    return createCandlestick(dataChart)
  },

  getOptionToPlot(chartType: ChartType, format: string): ChartOptions {
    return {
      ...chart.getDefaultChartOptions(format),
      ...((chartType === 'candlestick' || chartType === 'ohlc') && {
        series: {
          candlestick: {
            show: true,
            active: true,
            rangeWidth: 1,
            rangeColor: '#000',
            neutralColor: '#000',
            downColor: chartType === 'candlestick' ? '#fff' : '#000',
            upColor: '#000',
            bodyWidth: chartType === 'candlestick' ? '40%' : '20%',
            ohlc: chartType === 'candlestick' ? 0 : 1,
          },
          color: '#000',
        },
      }),
    }
  },

  getDefaultChartOptions(format: string): ChartOptions {
    return {
      xaxis: {
        mode: 'time',
        tickFormatter(val) {
          const d = new Date(val)
          return formatDate(d, format)
        },
        tickLength: 0,
      },
      yaxis: {
        tickFormatter(val) {
          return formatNumber(val, 4)
        },
      },
      series: {
        lines: {
          show: true,
          lineWidth: 1,
        },
        points: {
          show: false,
        },
        color: '#225763',
      },
      grid: {
        show: true,
        borderWidth: 0,
        hoverable: true,
      },
    }
  },
}

export default chart
